import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../config/api';
import Icon from '../components/Icon';
import { toast } from '../components/ToastProvider';
import '../styles/SearchSalons.css';

const SERVICES = [
  'Strzyżenie damskie', 'Strzyżenie męskie', 'Farbowanie',
  'Baleyage', 'Keratyna', 'Modelowanie', 'Golenie brody',
];

const SearchSalons = () => {
  const { gender } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [city, setCity] = useState('');
  const [salons, setSalons] = useState([]);
  const [searched, setSearched] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (!user) return;
    apiFetch('/b2b/favorites')
      .then(data => setFavorites(data || []))
      .catch(() => {});
  }, [user]);
  
  useEffect(() => {
    if (gender && (gender === 'male' || gender === 'female')) {
      localStorage.setItem('userGenderPreference', gender);
    }
  }, [gender]);
  
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filterRating, setFilterRating] = useState(0);
  const [filterService, setFilterService] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const availableCities = [
    'Rzeszów', 'Krosno', 'Przemyśl', 'Stalowa Wola', 
    'Mielec', 'Jasło', 'Sanok', 'Tarnobrzeg', 'Dębica'
  ];

  const getSalonRating = (salon) => ({
    average: parseFloat(salon.avg_rating || 0).toFixed(1),
    count:   parseInt(salon.review_count || 0),
  });

  const [, setSearching] = useState(false);

  const filterByGender = (salon) => {
    if (!gender) return true;
    if (!salon.category || salon.category === 'both') return true;
    return salon.category === gender;
  };

  const handleSearch = async (e, overrideCity) => {
    if (e) e.preventDefault();
    const searchCity = overrideCity || city;
    if (!searchCity.trim()) return;
    setSearching(true);
    try {
      const data = await apiFetch(`/salons?city=${encodeURIComponent(searchCity)}`);
      setSalons(data || []);
      setSearched(true);
      setShowSuggestions(false);
    } catch {
      toast.error('Błąd podczas wyszukiwania salonów');
    } finally {
      setSearching(false);
    }
  };

  const handleCityInput = (e) => {
    const value = e.target.value;
    setCity(value);
    if (value.length > 0) {
      const filtered = availableCities.filter(c => 
        c.toLowerCase().startsWith(value.toLowerCase())
      );
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setCity(suggestion);
    setShowSuggestions(false);
    handleSearch(null, suggestion);
  };

  const handleSalonClick = (salon) => {
    if (user && user.role !== 'client') return; 
    navigate(`/booking/${salon.id}`, { state: { salon, gender } });
  };

  const toggleFavorite = async (e, salon) => {
    e.stopPropagation();
    if (!user) { toast.info('Zaloguj się, aby dodawać do ulubionych'); return; }
    const isAlreadyFav = isFavorite(salon);
    try {
      if (isAlreadyFav) {
        await apiFetch(`/b2b/favorites/${encodeURIComponent(salon.name)}`, { method: 'DELETE' });
        setFavorites(prev => prev.filter(f => f.salon_name !== salon.name));
        toast.info('Usunięto z ulubionych');
      } else {
        const newFav = await apiFetch('/b2b/favorites', {
          method: 'POST',
          body: JSON.stringify({ salonId: salon.id, salonName: salon.name, salonCity: salon.city }),
        });
        setFavorites(prev => [...prev, newFav || { salon_name: salon.name, salon_city: salon.city }]);
        toast.success(`Dodano do ulubionych: ${salon.name}`);
      }
    } catch {
      toast.error('Błąd podczas aktualizacji ulubionych');
    }
  };

  const isFavorite = (salon) => favorites.some(f => f.salon_name === salon.name || f.name === salon.name);

  return (
    <div className={`search-container ${gender === 'female' ? 'female' : ''}`}>
      <div className="search-wrapper">
        <div className="header">
          <h1>Znajdź salon {gender === 'male' ? 'dla mężczyzn' : 'dla kobiet'}</h1>
          <p>Wpisz nazwę swojego miasta, aby znaleźć najbliższe salony</p>
        </div>

        <form onSubmit={handleSearch} className="search-form">
          <div className="search-box">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Wpisz nazwę miasta..."
                value={city}
                onChange={handleCityInput}
                onFocus={() => city.length > 0 && setShowSuggestions(true)}
                className="search-input"
                autoComplete="off"
              />
              {showSuggestions && suggestions.length > 0 && (
                <div className="autocomplete-suggestions">
                  {suggestions.map((suggestion, index) => (
                    <div key={index} className="suggestion-item" onClick={() => handleSuggestionClick(suggestion)}>
                      <Icon name="location" style={{marginRight:"8px",color:"#1e3c72"}} />{suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button type="submit" className="search-button">
              <Icon name="search" style={{marginRight:"8px"}} />Szukaj
            </button>
          </div>
        </form>

        {searched && (
          <div className="results-section">
            {salons.filter(filterByGender).length > 0 ? (
              <>
                <div className="search-filter-bar">
                  <div className="search-filter-left">
                    <h2>Salony w mieście: <strong>{city}</strong></h2>
                    <span className="search-count">{(() => {
                      const fc = salons.filter(filterByGender).filter(s => {
                        const r = getSalonRating(s);
                        if (filterRating > 0 && r.average < filterRating) return false;
                        return true;
                      }).length;
                      return `${fc} salonów`;
                    })()}</span>
                  </div>
                  <button className={`search-filter-toggle ${showFilters ? 'active' : ''}`} onClick={() => setShowFilters(f => !f)}>
                    <Icon name="filter" style={{marginRight:'7px'}} />Filtry
                    {(filterRating > 0 || filterService || filterOpen) && <span className="filter-active-dot" />}
                  </button>
                </div>

                {showFilters && (
                  <div className="search-filters-panel">
                    <div className="search-filter-group">
                      <label><Icon name="star" color="#f59e0b" style={{marginRight:'6px'}} />Minimalna ocena</label>
                      <div className="filter-stars">
                        {[0,3,4,4.5].map(v => (
                          <button key={v} className={`filter-star-btn ${filterRating === v ? 'active' : ''}`} onClick={() => setFilterRating(v)}>
                            {v === 0 ? 'Wszystkie' : `${v}+★`}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="search-filter-group">
                      <label><Icon name="service" style={{marginRight:'6px'}} />Usługa</label>
                      <div className="filter-services">
                        <button className={`filter-service-btn ${!filterService ? 'active' : ''}`} onClick={() => setFilterService('')}>Wszystkie</button>
                        {SERVICES.map(s => (
                          <button key={s} className={`filter-service-btn ${filterService === s ? 'active' : ''}`} onClick={() => setFilterService(s === filterService ? '' : s)}>{s}</button>
                        ))}
                      </div>
                    </div>
                    <div className="search-filter-group">
                      <label><Icon name="clock" style={{marginRight:'6px'}} />Dostępność</label>
                      <div className="filter-stars">
                        <button className={`filter-star-btn ${!filterOpen ? 'active' : ''}`} onClick={() => setFilterOpen(false)}>Wszystkie</button>
                        <button className={`filter-star-btn ${filterOpen ? 'active' : ''}`} onClick={() => setFilterOpen(true)}>Teraz otwarte</button>
                      </div>
                    </div>
                    {(filterRating > 0 || filterService || filterOpen) && (
                      <button className="filter-clear-btn" onClick={() => { setFilterRating(0); setFilterService(''); setFilterOpen(false); }}>
                        <Icon name="close" style={{marginRight:'6px'}} />Wyczyść filtry
                      </button>
                    )}
                  </div>
                )}

                <div className="salons-grid">
                  {salons
                    .filter(filterByGender)
                    .filter(s => {
                      const r = getSalonRating(s);
                      if (filterRating > 0 && r.average < filterRating) return false;
                      if (filterOpen) { const now = new Date(); const h = now.getHours() + now.getMinutes()/60; if (h < 9 || h > 18) return false; }
                      return true;
                    })
                    .sort((a, b) => getSalonRating(b).average - getSalonRating(a).average)
                    .map((salon) => {
                      const rating = getSalonRating(salon);
                      const fav = isFavorite(salon);
                      return (
                        <div key={salon.id} className="salon-card" onClick={() => handleSalonClick(salon)}>
                          <div className="salon-image" style={{backgroundImage: salon.photo ? `url(${salon.photo})` : 'none', backgroundSize:'cover', backgroundPosition:'center', backgroundRepeat:'no-repeat'}}>
                            {!salon.photo && <div className="salon-placeholder"><Icon name="scissors" size="2x" color="#1e3c72" /></div>}
                            {rating.count > 0 && (
                              <div className="salon-rating-badge">
                                <Icon name="star" color="#f59e0b" style={{marginRight:'3px',fontSize:'0.85em'}} />{rating.average}
                              </div>
                            )}
                            {user && (
                              <button className={`fav-btn ${fav ? 'fav-active' : ''}`} onClick={(e) => toggleFavorite(e, salon)} title={fav ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'}>
                                <Icon name={fav ? 'heart' : 'heart-empty'} color={fav ? '#ef4444' : '#999'} />
                              </button>
                            )}
                          </div>
                          <div className="salon-info">
                            <h3>{salon.name}</h3>
                            <p className="salon-address"><Icon name="location" color="#1e3c72" style={{marginRight:'5px'}} />{salon.address}</p>
                            <div className="salon-rating">
                              {rating.count > 0 ? (
                                <div className="salon-stars-row">
                                  {[1,2,3,4,5].map(i => (
                                    <Icon key={i} name={i <= Math.round(rating.average) ? 'star' : 'star-empty'} color={i <= Math.round(rating.average) ? '#f59e0b' : '#d1d5db'} style={{fontSize:'0.9em'}} />
                                  ))}
                                  <span className="rating-num">{rating.average}</span>
                                  <span className="review-count">({rating.count})</span>
                                </div>
                              ) : (
                                <span className="no-reviews"><Icon name="star-empty" color="#ccc" style={{marginRight:'4px'}} />Brak opinii</span>
                              )}
                            </div>
                            {salon.description && (
                              <p className="salon-description">{salon.description}</p>
                            )}
                            {(salon.instagram || salon.facebook) && (
                              <div className="salon-socials">
                                {salon.instagram && (
                                  <a href={salon.instagram.startsWith('http') ? salon.instagram : `https://instagram.com/${salon.instagram.replace('@','')}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="salon-social-link salon-social-ig"
                                    onClick={e => e.stopPropagation()}>
                                    <Icon name="instagram" style={{marginRight:'4px',fontSize:'0.85em'}} />{salon.instagram}
                                  </a>
                                )}
                                {salon.facebook && (
                                  <a href={salon.facebook.startsWith('http') ? salon.facebook : `https://${salon.facebook}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="salon-social-link salon-social-fb"
                                    onClick={e => e.stopPropagation()}>
                                    <Icon name="facebook" style={{marginRight:'4px',fontSize:'0.85em'}} />{salon.facebook}
                                  </a>
                                )}
                              </div>
                            )}
                            {filterService && (
                              <div className="salon-service-match">
                                <Icon name="check" color="#10b981" style={{marginRight:'5px'}} />{filterService}
                              </div>
                            )}
                            <button className="book-button"><Icon name="calendar-day" style={{marginRight:'7px'}} />Zarezerwuj wizytę</button>
                            {user && user.role !== 'client' && (
                              <p className="no-book-note"><Icon name="warning" color="#ef4444" style={{marginRight:'5px'}} />Tylko klienci mogą rezerwować</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </>
            ) : (
              <div className="no-results">
                <h2>Nie znaleziono salonów</h2>
                <p>Spróbuj wpisać miasto z Podkarpacia (np. Rzeszów, Krosno, Przemyśl, Stalowa Wola, Mielec, Jasło, Sanok)</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchSalons;